/**
 * MyInterviewsPage.tsx
 * V55.35: 면담 기록 페이지
 *
 * URL: /my/interviews
 *
 * 기능:
 * - 본인에게 전송된 면담 기록만 표시 (서버에서 auth.uid 고정)
 * - deliveryStatus: sent, agreed, disagreed만 표시 (notSent 제외)
 * - 1차: 리스트만 표시 (동의/미동의 버튼은 추후 개발)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import {
  useMyReceivedInterviews,
  Interview,
  DELIVERY_STATUS_LABELS,
  DeliveryStatus,
} from '../../shared/hooks/useInterviews';

export function MyInterviewsPage() {
  const { interviews, isLoading, error, refetch } = useMyReceivedInterviews();

  // 에러 UI
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
          면담 기록
        </h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: 'var(--color-red-50, #fef2f2)',
            border: '1px solid var(--color-red-200, #fecaca)',
            borderRadius: '8px',
            color: 'var(--color-red-700, #b91c1c)',
          }}
        >
          <AlertCircle size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>데이터 조회 실패</div>
            <div style={{ fontSize: '14px', marginTop: '4px' }}>
              {error.message || '면담 기록을 불러올 수 없습니다.'}
            </div>
          </div>
          <button
            onClick={refetch}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              background: 'var(--color-red-100, #fee2e2)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            <RefreshCw size={14} />
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  // 로딩 UI
  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
          면담 기록
        </h1>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            color: '#64748b',
          }}
        >
          <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '8px' }}>로딩 중...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
          면담 기록
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          면담자가 전송한 면담 기록을 확인할 수 있습니다.
        </p>
      </div>

      {/* 빈 상태 */}
      {interviews.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            padding: '40px 20px',
            textAlign: 'center',
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
          }}
        >
          <FileText size={64} style={{ color: '#cbd5e1', marginBottom: '20px' }} />
          <div style={{ fontSize: '16px', color: '#64748b', marginBottom: '8px' }}>
            전송받은 면담기록이 없습니다.
          </div>
          <div style={{ fontSize: '14px', color: '#94a3b8' }}>
            면담자가 면담 기록을 전송하면 이곳에 표시됩니다.
          </div>
        </div>
      ) : (
        /* 면담 목록 테이블 */
        <div
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={thStyle}>면담자</th>
                <th style={thStyle}>면담일</th>
                <th style={thStyle}>면담시간</th>
                <th style={thStyle}>면담장소</th>
                <th style={thStyle}>면담타입</th>
                <th style={thStyle}>상태</th>
              </tr>
            </thead>
            <InterviewTableBody interviews={interviews} />
          </table>
        </div>
      )}

      {/* 안내 메시지 */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          background: '#f8fafc',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#64748b',
        }}
      >
        <strong>V55.37:</strong> 면담을 클릭하면 상세 내용을 확인하고 동의/미동의 할 수 있습니다.
      </div>
    </div>
  );
}

// ============================================
// 테이블 바디 컴포넌트 (V55.37: 네비게이션 포함)
// ============================================

interface InterviewTableBodyProps {
  interviews: Interview[];
}

function InterviewTableBody({ interviews }: InterviewTableBodyProps) {
  const navigate = useNavigate();

  const handleRowClick = (interviewId: string) => {
    navigate(`/my/interviews/${interviewId}`);
  };

  return (
    <tbody>
      {interviews.map((interview) => (
        <InterviewRow
          key={interview.id}
          interview={interview}
          onClick={() => handleRowClick(interview.id)}
        />
      ))}
    </tbody>
  );
}

// ============================================
// 면담 행 컴포넌트
// ============================================

interface InterviewRowProps {
  interview: Interview;
  onClick: () => void;
}

function InterviewRow({ interview, onClick }: InterviewRowProps) {
  // 날짜 포맷팅
  const dateStr = interview.interviewDate?.replace(/-/g, '.') || '-';

  // 타입 라벨
  // V62: typeLabel(BE resolveTypeLabel → DB name 스냅샷) 전용 — "면담 목적 = 면담 타입명" 정책
  const typeLabel = interview.typeLabel || '-';

  // 전송 상태
  const deliveryStatus = (interview.deliveryStatus || 'sent') as DeliveryStatus;
  const statusLabel = DELIVERY_STATUS_LABELS[deliveryStatus];

  // 상태별 색상
  const statusColors: Record<DeliveryStatus, { color: string; bg: string }> = {
    notSent: { color: '#64748b', bg: '#f1f5f9' },
    sent: { color: '#3b82f6', bg: '#dbeafe' },
    agreed: { color: '#22c55e', bg: '#dcfce7' },
    disagreed: { color: '#ef4444', bg: '#fee2e2' },
  };
  const { color, bg } = statusColors[deliveryStatus] || statusColors.sent;

  return (
    <tr
      style={{
        borderTop: '1px solid #e2e8f0',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {/* 면담자 */}
      <td style={tdStyle}>{interview.interviewerName || '-'}</td>

      {/* 면담일 */}
      <td style={tdStyle}>{dateStr}</td>

      {/* 면담시간 */}
      <td style={tdStyle}>{interview.interviewTime || '-'}</td>

      {/* 면담장소 */}
      <td style={tdStyle}>{interview.location || '-'}</td>

      {/* 면담타입 */}
      <td style={tdStyle}>{typeLabel}</td>

      {/* 상태 */}
      <td style={tdStyle}>
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            color,
            background: bg,
          }}
        >
          {statusLabel}
        </span>
      </td>
    </tr>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#475569',
  borderBottom: '1px solid #e2e8f0',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  color: '#1e293b',
};

export default MyInterviewsPage;
